import React, { useEffect, useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { fetchVehicleChecks, fetchDispatchReservations } from '../api/dispatchService';
import { useVehicleStatusUpdate } from '../hooks/useVehicleStatusUpdate';
import { updateVehicleTypePriority } from '../api/vehicleTypeService';
import { updateVehicle } from '../api/vehicleService';
import VehicleDetailPanel from '../components/VehicleDetailPanel';
import VehicleOrderDialog from '../components/VehicleOrderDialog';
import { useAuth } from '../components/AuthContext';
import { useReservationData } from '../context/ReservationDataContext';
import { useStatusMaster } from '../hooks/useStatusMaster';
import { loadFromCookie, saveJsonToCookie } from '../utils/cookieUtils';
import { usePanelUrlSync, findVehicleByParam } from '../hooks/usePanelUrlSync';
import { buildVehicleCsv, downloadCsv } from '../lib/vehicleCsv';


import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Typography, Box, CircularProgress, Snackbar, Alert, Checkbox, Button,
  Dialog, DialogTitle, DialogContent, Grid, Autocomplete, TextField,
  ToggleButton, ToggleButtonGroup, Stack, DialogActions, DialogContentText,
  Divider, Chip, Popover, Menu, MenuItem, FormControlLabel, useTheme, useMediaQuery
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SortIcon from '@mui/icons-material/Sort';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DownloadIcon from '@mui/icons-material/Download';

// CHECK_FIELDS removed. Using dynamic checks from API.

// クッキーキー
const RESERVATION_FILTER_COOKIE_KEY = 'vehiclelist_reservation_filter';

const loadReservationFilterFromCookie = () => loadFromCookie(RESERVATION_FILTER_COOKIE_KEY, null);
const saveReservationFilterToCookie = (filterData: any) => saveJsonToCookie(RESERVATION_FILTER_COOKIE_KEY, filterData, 30);



// ソート関数
const sortVehicles = (vehicleList: any[]): any[] => {
  return [...vehicleList].sort((a, b) => {
    const priorityA = a.vehicle_type?.vehicle_type_priority ?? 0;
    const priorityB = b.vehicle_type?.vehicle_type_priority ?? 0;
    if (priorityA !== priorityB) {
      return priorityB - priorityA;
    }
    return (a.vehicle_code || '').localeCompare(b.vehicle_code || '');
  });
};

export default function VehicleList(): React.ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { vehicles: contextVehicles, reservations: contextReservations, isLoadingVehicles } = useReservationData() as any;
  // loadVehicles is a no-op: vehicle data is synced from context
  const loadVehicles = useCallback(async () => {}, []);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [vehiclePanelDirty, setVehiclePanelDirty] = useState(false);
  const [statusModal, setStatusModal] = useState<{ open: boolean; vehicle: any }>({ open: false, vehicle: null });
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: string }>({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; vehicle: any; statusId: any }>({ open: false, vehicle: null, statusId: null });
  const [unsavedConfirm, setUnsavedConfirm] = useState<any>(null);
  const { vehicleStatusList, locationStatusList, reservationStatusList } = useStatusMaster({ locationNullLabel: '未指定' });
  const [locationStatusModal, setLocationStatusModal] = useState<{ open: boolean; vehicle: any }>({ open: false, vehicle: null });
  const [vehicleChecks, setVehicleChecks] = useState<any[]>([]); // Added vehicleChecks state

  // Filters
  const [statusFilter, setStatusFilter] = useState<any[]>([]);
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState<any[]>([]);
  const [completionFilter, setCompletionFilter] = useState('all'); // 'all', 'ok', 'incomplete'

  // ソート: 次回出発 / チェック状況（クッキーに保存）
  const [departureSortMode, setDepartureSortMode] = useState<'off' | 'asc' | 'desc'>(() => {
    try { return (loadFromCookie('vehiclelist_sort_departure', 'off') as any) || 'off'; } catch { return 'off'; }
  });
  const [checkSortOn, setCheckSortOn] = useState(() => {
    try { return loadFromCookie('vehiclelist_sort_check', false) as boolean; } catch { return false; }
  });

  // ソート変更時にクッキー保存
  useEffect(() => { saveJsonToCookie('vehiclelist_sort_departure', departureSortMode, 30); }, [departureSortMode]);
  useEffect(() => { saveJsonToCookie('vehiclelist_sort_check', checkSortOn, 30); }, [checkSortOn]);

  // 車両が出発中かどうか判定（label に「出発中」を含む、または code === 'in_use'）
  const departureStatusIds = useMemo(() => {
    return new Set(
      vehicleStatusList
        .filter((st: any) => st.label?.includes('出発中') || st.code === 'in_use')
        .map((st: any) => st.id)
    );
  }, [vehicleStatusList]);
  const isVehicleInUse = useCallback((vehicle: any) => {
    return departureStatusIds.has(vehicle.status);
  }, [departureStatusIds]);

  // 予約ベースのフィルタ
  // デフォルト: 明日〜明後日（クッキーに保存されていればそちらを優先）
  const getDefaultDateFrom = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };
  const getDefaultDateTo = () => {
    const dayAfterTomorrow = new Date();
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
    return dayAfterTomorrow.toISOString().split('T')[0];
  };

  // クッキーから読み込み
  const savedFilter = useMemo(() => loadReservationFilterFromCookie(), []);

  const [rentalDateFrom, setRentalDateFrom] = useState(() => savedFilter?.dateFrom || getDefaultDateFrom());
  const [rentalDateTo, setRentalDateTo] = useState(() => savedFilter?.dateTo || getDefaultDateTo());
  // reservationStatusFilter 廃止（車両ステータスフィルタに統一）
  const [reservationVehicleIds, setReservationVehicleIds] = useState<Set<number> | null>(null); // null = フィルタ無効, Set = 有効
  const [reservationFilterLoading, setReservationFilterLoading] = useState(false);
  const [dateRangeAnchor, setDateRangeAnchor] = useState<HTMLElement | null>(null); // 日付範囲ポップオーバー用
  // statusMenuAnchor 廃止
  const [orderDialogOpen, setOrderDialogOpen] = useState(false); // 車両順番ダイアログ
  const [csvMenuAnchor, setCsvMenuAnchor] = useState<HTMLElement | null>(null); // CSV ダウンロードメニュー

  const { user } = useAuth();


  // 車両タイプの一覧を取得（重複なし）
  const vehicleTypeOptions = useMemo(() => {
    const typeMap = new Map();
    vehicles.forEach(v => {
      if (v.vehicle_type?.id) {
        typeMap.set(v.vehicle_type.id, {
          id: v.vehicle_type.id,
          name: v.vehicle_type.vehicle_type_name || v.vehicle_type.vehicle_type_code || '-',
          code: v.vehicle_type.vehicle_type_code || '',
          priority: v.vehicle_type.vehicle_type_priority ?? 0,
        });
      }
    });
    // priorityで降順ソート
    return Array.from(typeMap.values()).sort((a, b) => b.priority - a.priority);
  }, [vehicles]);

  // Context から vehicles を同期（ソート付き）
  useEffect(() => {
    const list = Object.values(contextVehicles);
    if (list.length > 0) {
      setVehicles(sortVehicles(list));
      setLoading(false);
    } else if (!isLoadingVehicles) {
      setLoading(false);
    }
  }, [contextVehicles, isLoadingVehicles]);


  // 車両チェックマスター取得
  useEffect(() => {
    (async () => {
      try {
        const checks = await fetchVehicleChecks();
        setVehicleChecks(checks);
      } catch (error) {
        console.error('Failed to fetch vehicle checks:', error);
      }
    })();
  }, []);

  // 必須チェック項目のみ抽出（表示順序でソート）
  const requiredChecks = useMemo(() => {
    return vehicleChecks
      .filter(c => c.require_flg)
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [vehicleChecks]);

  // 各車両の次回出発予約 + 直近返却を計算
  const EXCLUDE_STATUSES = new Set(['cancelled', 'nonreserved', 'full', 'offer', '4', '5', '6', '7']);
  const nextDepartureMap = useMemo(() => {
    const map: Record<string, { start_date: string; customer_name: string; return_date?: string } | null> = {};
    if (!contextReservations || !Array.isArray(contextReservations)) return map;

    const now = dayjs();
    const todayStart = now.startOf('day');
    const reservations = Array.isArray(contextReservations) ? contextReservations : [];

    vehicles.forEach((v: any) => {
      const vehicleRes = reservations.filter((r: any) => {
        if (Number(r.vehicle_id) !== Number(v.id)) return false;
        if (EXCLUDE_STATUSES.has(String(r.status))) return false;
        return true;
      });

      // 次回出発: start_date が今日以降で最も早い予約
      const futureRes = vehicleRes
        .filter((r: any) => { const s = dayjs(r.start_date); return s.isValid() && !s.isBefore(todayStart); })
        .sort((a: any, b: any) => dayjs(a.start_date).unix() - dayjs(b.start_date).unix());

      if (futureRes.length === 0) {
        map[v.id] = null;
        return;
      }

      const next = futureRes[0];

      // 直近返却: 次回出発の前に返却される予約（end_date が今以降で、次回出発の start_date 以前）
      const nextStart = dayjs(next.start_date);
      const returningRes = vehicleRes
        .filter((r: any) => {
          if (r.id === next.id) return false; // 同じ予約は除外
          const e = dayjs(r.end_date);
          return e.isValid() && !e.isBefore(todayStart) && e.isBefore(nextStart.add(1, 'day'));
        })
        .sort((a: any, b: any) => dayjs(a.end_date).unix() - dayjs(b.end_date).unix());

      const returnRes = returningRes.length > 0 ? returningRes[returningRes.length - 1] : null;

      map[v.id] = {
        start_date: next.start_date,
        customer_name: next.customer_name || '',
        return_date: returnRes?.end_date || undefined,
      };
    });

    return map;
  }, [contextReservations, vehicles]);

  // 予約ステータスフィルタ廃止（車両ステータスに統一済み）

  const handleRowClick = (vehicle: any) => {
    // 現在のパネルが開いていて未保存の場合は確認
    if (selectedVehicle && vehiclePanelDirty) {
      setUnsavedConfirm({ targetVehicle: vehicle });
      return;
    }
    setSelectedVehicle(vehicle);
    setVehiclePanelDirty(false);
  };

  const handleClosePanel = () => {
    // 未保存の場合は確認
    if (vehiclePanelDirty) {
      setUnsavedConfirm({ targetVehicle: null });
      return;
    }
    setSelectedVehicle(null);
    setVehiclePanelDirty(false);
  };

  // ---- 車両パネル URL 同期（?vehicle=）----
  // 開閉は既存ハンドラ経由（未保存確認ダイアログの挙動を維持）。仕様: feature-sidepanel.md「URL連動」
  usePanelUrlSync<any>({
    paramKey: 'vehicle',
    current: selectedVehicle,
    getUrlValue: (v) => String(v?.id ?? '') || null,
    resolve: (val) => findVehicleByParam(vehicles, val),
    isReady: vehicles.length > 0,
    onOpen: (v) => handleRowClick(v),
    onClose: () => handleClosePanel(),
    onNotFound: (v) => setToast({ open: true, message: `車両 ${v} が見つかりません`, severity: 'error' }),
  });

  // 未保存確認ダイアログのアクション処理
  const handleUnsavedConfirmAction = (action: string) => {
    if (action === 'cancel') {
      setUnsavedConfirm(null);
      return;
    }
    if (action === 'discard') {
      const targetVehicle = unsavedConfirm?.targetVehicle;
      setUnsavedConfirm(null);
      setVehiclePanelDirty(false);
      setSelectedVehicle(targetVehicle);
    }
  };

  const handleSaveVehicle = async (updatedVehicle: any) => {
    try {
      const result = await updateVehicle(updatedVehicle, user?.id);
      if (result) {
        setToast({ open: true, message: '車両情報を更新しました', severity: 'success' });
        // リストを再取得して最新状態にする
        await loadVehicles();
        // 選択中の車両も更新（パネルを開いたままにする場合）
        setSelectedVehicle(result);
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      console.error('Failed to update vehicle:', error);
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
    }
  };

  const handleDynamicCheckChange = async (e: any, vehicle: any, checkId: any) => {
    e.stopPropagation();
    // ボタンクリック: 現在値をトグル
    const currentValue = isCheckDone(vehicle, checkId);
    const newValue = !currentValue;

    // 現在の状況を取得
    const currentSituations = vehicle.check_situations || [];
    const index = currentSituations.findIndex(c => c.id === checkId);

    let newSituations;
    if (index >= 0) {
      newSituations = [...currentSituations];
      newSituations[index] = { ...newSituations[index], value: newValue };
    } else {
      newSituations = [...currentSituations, { id: checkId, value: newValue }];
    }

    // Optimistic update
    setVehicles(prev => prev.map(v =>
      v.id === vehicle.id ? { ...v, check_situations: newSituations } : v
    ));

    try {
      await updateVehicle({
        id: vehicle.id,
        check_situations: [{ id: checkId, value: newValue }]
      }, user?.id);
    } catch (error) {
      console.error('Failed to update vehicle check:', error);
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
      // Revert on failure
      setVehicles(prev => prev.map(v =>
        v.id === vehicle.id ? { ...v, check_situations: currentSituations } : v
      ));
    }
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  // Helper to check if a specific check is done
  const isCheckDone = (vehicle: any, checkId: any): boolean => {
    const situation = vehicle.check_situations?.find(c => c.id === checkId);
    return situation?.value === true;
  };

  const isAllChecked = useCallback((vehicle) => {
    if (requiredChecks.length === 0) return true;
    return requiredChecks.every(check => isCheckDone(vehicle, check.id));
  }, [requiredChecks]);

  const handleAllOk = async (e: React.MouseEvent, vehicle: any) => {
    e.stopPropagation();
    if (isAllChecked(vehicle)) return;

    // 必須項目のIDとtrueのペアを作成
    const updates = requiredChecks.map(check => ({ id: check.id, value: true }));

    // Optimistic Logic
    const currentSituations = vehicle.check_situations || [];
    const newSituations = [...currentSituations];

    updates.forEach(update => {
      const idx = newSituations.findIndex(s => s.id === update.id);
      if (idx >= 0) {
        newSituations[idx] = { ...newSituations[idx], value: true };
      } else {
        newSituations.push(update);
      }
    });

    setVehicles(prev => prev.map(v =>
      v.id === vehicle.id ? { ...v, check_situations: newSituations } : v
    ));

    try {
      await updateVehicle({ id: vehicle.id, check_situations: updates }, user?.id);
      setToast({ open: true, message: '全ての項目を完了にしました', severity: 'success' });
    } catch (error) {
      console.error('Failed to update vehicle all ok:', error);
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
      // Revert
      setVehicles(prev => prev.map(v =>
        v.id === vehicle.id ? vehicle : v
      ));
    }
  };

  // 全チェックをリセット
  const handleResetAll = async (e: React.MouseEvent, vehicle: any) => {
    e.stopPropagation();
    const updates = requiredChecks.map(check => ({ id: check.id, value: false }));
    const currentSituations = vehicle.check_situations || [];
    const newSituations = [...currentSituations];
    updates.forEach(update => {
      const idx = newSituations.findIndex(s => s.id === update.id);
      if (idx >= 0) {
        newSituations[idx] = { ...newSituations[idx], value: false };
      } else {
        newSituations.push(update);
      }
    });
    setVehicles(prev => prev.map(v =>
      v.id === vehicle.id ? { ...v, check_situations: newSituations } : v
    ));
    try {
      await updateVehicle({ id: vehicle.id, check_situations: updates }, user?.id);
      setToast({ open: true, message: 'チェックをリセットしました', severity: 'info' });
    } catch (error) {
      console.error('Failed to reset vehicle checks:', error);
      setToast({ open: true, message: 'リセットに失敗しました', severity: 'error' });
      setVehicles(prev => prev.map(v => v.id === vehicle.id ? vehicle : v));
    }
  };

  const handleStatusClick = (e: React.MouseEvent, vehicle: any) => {
    e.stopPropagation();
    setStatusModal({ open: true, vehicle });
  };

  const handleLocationStatusClick = (e: React.MouseEvent, vehicle: any) => {
    e.stopPropagation();
    setLocationStatusModal({ open: true, vehicle });
  };

  const { executeStatusUpdate } = useVehicleStatusUpdate({
    userId: user?.id,
    onOptimisticUpdate: useCallback((vehicle, updatedVehicle) => {
      setVehicles(prev => prev.map(v => v.id === vehicle.id ? updatedVehicle : v));
    }, []),
    onRevert: useCallback((vehicle) => {
      setVehicles(prev => prev.map(v => v.id === vehicle.id ? vehicle : v));
    }, []),
    onSuccess: useCallback(() => {
      setToast({ open: true, message: 'ステータスを更新しました', severity: 'success' });
    }, []),
    onError: useCallback(() => {
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
    }, []),
  });

  const handleStatusUpdate = (statusId: number) => {
    const vehicle = statusModal.vehicle;
    if (!vehicle) return;

    setStatusModal({ open: false, vehicle: null });

    if (statusId === 1 && vehicle.status !== 1) {
      setConfirmDialog({ open: true, vehicle, statusId });
      return;
    }

    const statusObj = vehicleStatusList.find(s => s.id === statusId);
    executeStatusUpdate(vehicle, statusId as any, { status: statusId as any, status_name: statusObj?.label ?? '' });
  };

  const handleLocationStatusUpdate = async (statusId: any) => {
    const vehicle = locationStatusModal.vehicle;
    if (!vehicle) return;

    setLocationStatusModal({ open: false, vehicle: null });

    // Optimistic update
    setVehicles(prev => prev.map(v =>
      v.id === vehicle.id ? { ...v, location_status: statusId } : v
    ));

    try {
      await updateVehicle({ id: vehicle.id, location_status: statusId }, user?.id);
      setToast({ open: true, message: '位置ステータスを更新しました', severity: 'success' });
      // ロードして最新の状態（name等）を取得
      await loadVehicles();
    } catch (error) {
      console.error('Failed to update vehicle location status:', error);
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
      await loadVehicles();
    }
  };

  const handleConfirmClose = (_resetChecks?: boolean) => {
    const { vehicle, statusId } = confirmDialog;
    setConfirmDialog({ open: false, vehicle: null, statusId: null });
    if (vehicle && statusId) {
      const statusObj = vehicleStatusList.find(s => s.id === statusId);
      executeStatusUpdate(vehicle, statusId, { status: statusId, status_name: statusObj?.label ?? '' });
    }
  };

  // 予約フィルターを適用
  const applyReservationFilter = async () => {
    if (!rentalDateFrom || !rentalDateTo) {
      setToast({ open: true, message: '期間を指定してください', severity: 'warning' });
      return;
    }

    setReservationFilterLoading(true);
    try {
      console.log('[VehicleList] Fetching reservations with params:', {
        start_date: rentalDateFrom,
        end_date: `${rentalDateTo} 23:59:59`,
      });

      // 期間内の予約を取得（start_date/end_dateで期間指定）
      // APIリクエスト時に終了日の23:59:59まで指定することで、同日の遅い時間の予約も「重なる予約」として取得できるようにする
      const reservations = await fetchDispatchReservations({
        start_date: rentalDateFrom,
        end_date: `${rentalDateTo} 23:59:59`,
      });

      console.log('[VehicleList] Fetched reservations (overlapping):', reservations.length);

      // 追加ロジック: 開始日が範囲内の予約のみに絞り込む
      // APIは期間に重なる予約を全て返すが、ユーザーは「期間内に始まる」予約のみを求めているため
      // 時間を含めたレンジで比較する (YYYY-MM-DD 00:00:00 ～ YYYY-MM-DD 23:59:59)
      const rangeStart = `${rentalDateFrom} 00:00:00`;
      const rangeEnd = `${rentalDateTo} 23:59:59`;

      const reservationsStartingInRange = reservations.filter(r => {
        if (!r.start_date) return false;

        // 比較用にフォーマットを統一 (T区切りをスペースにして、秒までで切り取る)
        // 例: "2025-12-24T10:00:00+09:00" -> "2025-12-24 10:00:00"
        const targetDate = r.start_date.replace('T', ' ').substring(0, 19);

        return targetDate >= rangeStart && targetDate <= rangeEnd;
      });

      console.log('[VehicleList] Filtered reservations (starting in range):', reservationsStartingInRange.length);

      console.log('[VehicleList] Fetched reservations:', reservations.length);
      console.log('[VehicleList] First 3 reservations:', reservations.slice(0, 3));

      const filtered = reservationsStartingInRange;

      // vehicle_idの一覧を作成（数値に変換して統一）
      // out_of_useでない（有効な）車両のみを対象
      const activeVehicleIdSet = new Set(
        vehicles.filter(v => v.out_of_use !== true).map(v => v.id)
      );
      const vehicleIds = new Set(
        filtered
          .map(r => r.vehicle_id)
          .filter(Boolean)
          .map(id => Number(id))
          .filter(id => activeVehicleIdSet.has(id)) // 有効な車両のみ
      );
      console.log('[VehicleList] Vehicle IDs found:', vehicleIds.size, [...vehicleIds].slice(0, 5));
      setReservationVehicleIds(vehicleIds);

      // クッキーに保存
      saveReservationFilterToCookie({
        dateFrom: rentalDateFrom,
        dateTo: rentalDateTo,
        statusCodes: [],
      });

      setToast({ open: true, message: `${vehicleIds.size}台の車両が見つかりました`, severity: 'success' });
    } catch (error) {
      console.error('Failed to fetch reservations:', error);
      setToast({ open: true, message: '予約データの取得に失敗しました', severity: 'error' });
    } finally {
      setReservationFilterLoading(false);
    }
  };

  // 予約フィルターをクリア
  const clearReservationFilter = () => {
    setRentalDateFrom('');
    setRentalDateTo('');
    setReservationVehicleIds(null);
  };

  // 車両順番を保存
  const handleSaveVehicleOrder = async (updates: any[]) => {
    try {
      // 各車両の優先度を更新
      await Promise.all(
        updates.map(update =>
          updateVehicle({ id: update.id, priority: update.priority }, user?.id)
        )
      );

      setToast({ open: true, message: '車両の順番を更新しました', severity: 'success' });

      // 車両リストを再取得
      await loadVehicles();
    } catch (error) {
      console.error('Failed to update vehicle order:', error);
      throw error; // ダイアログ側でエラーハンドリング
    }
  };

  // 車両タイプ順番を保存
  const handleSaveVehicleTypeOrder = async (updates: any[]) => {
    try {
      // 各車両タイプの優先度を更新
      await Promise.all(
        updates.map(update =>
          updateVehicleTypePriority(update.id, update.priority)
        )
      );

      setToast({ open: true, message: '車両タイプの順番を更新しました', severity: 'success' });

      // 車両リストを再取得（車両タイプ情報も含まれる）
      await loadVehicles();
    } catch (error) {
      console.error('Failed to update vehicle type order:', error);
      throw error; // ダイアログ側でエラーハンドリング
    }
  };

  const retiredVehicles = vehicles.filter(v => v.out_of_use === true);
  const activeVehicles = vehicles.filter(v => v.out_of_use !== true);

  const filteredVehicles = activeVehicles.filter(vehicle => {
    // Status Filter
    if (statusFilter.length > 0) {
      const statusId = vehicle.status || 1; // Default to 1 if null
      if (!statusFilter.some(s => s.id === statusId)) {
        return false;
      }
    }

    // Vehicle Type Filter
    if (vehicleTypeFilter.length > 0) {
      const typeId = vehicle.vehicle_type?.id;
      if (!typeId || !vehicleTypeFilter.some(t => t.id === typeId)) {
        return false;
      }
    }

    // Completion Filter
    if (completionFilter === 'ok') {
      if (!isAllChecked(vehicle)) return false;
    } else if (completionFilter === 'incomplete') {
      if (isAllChecked(vehicle)) return false;
    }

    // Reservation Filter
    if (reservationVehicleIds !== null) {
      if (!reservationVehicleIds.has(vehicle.id)) {
        return false;
      }
    }

    return true;
  });

  // ソート適用
  const sortedVehicles = useMemo(() => {
    if (departureSortMode === 'off' && !checkSortOn) return filteredVehicles;

    return [...filteredVehicles].sort((a, b) => {
      // 1. 次回出発ソート（優先）
      if (departureSortMode !== 'off') {
        const nextA = nextDepartureMap[a.id];
        const nextB = nextDepartureMap[b.id];
        const timeA = nextA ? dayjs(nextA.start_date).unix() : Infinity;
        const timeB = nextB ? dayjs(nextB.start_date).unix() : Infinity;
        if (timeA !== timeB) {
          return departureSortMode === 'asc' ? timeA - timeB : timeB - timeA;
        }
      }

      // 2. チェック状況ソート
      if (checkSortOn) {
        const allA = isAllChecked(a);
        const allB = isAllChecked(b);
        // ALL OK済みは後ろ
        if (allA !== allB) return allA ? 1 : -1;

        // 未完了同士: チェック済み数が少ない方が前
        const doneCountA = requiredChecks.filter(c => isCheckDone(a, c.id)).length;
        const doneCountB = requiredChecks.filter(c => isCheckDone(b, c.id)).length;
        if (doneCountA !== doneCountB) return doneCountA - doneCountB;

        // 同数なら、requiredChecksの順序で最初の未完了チェックを比較
        for (const check of requiredChecks) {
          const dA = isCheckDone(a, check.id);
          const dB = isCheckDone(b, check.id);
          if (dA !== dB) return dA ? 1 : -1;
        }
      }

      return 0;
    });
  }, [filteredVehicles, departureSortMode, checkSortOn, nextDepartureMap, requiredChecks, isAllChecked, isCheckDone]);

  // CSV ダウンロード（列の組み立ては lib/vehicleCsv.ts）
  // 「表示中」は今のフィルタ・並び順のまま。「全車両」は利用外も含めた車両マスタ丸ごと
  const handleDownloadCsv = useCallback((scope: 'visible' | 'all') => {
    setCsvMenuAnchor(null);
    const target = scope === 'all' ? sortVehicles(vehicles) : sortedVehicles;
    const csv = buildVehicleCsv(target, vehicleChecks);
    const suffix = scope === 'all' ? 'all' : 'filtered';
    downloadCsv(csv, `vehicles_${suffix}_${dayjs().format('YYYYMMDD_HHmm')}.csv`);
  }, [vehicles, sortedVehicles, vehicleChecks]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          車両一覧
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={(e) => setCsvMenuAnchor(e.currentTarget)}
            size="medium"
          >
            CSV
          </Button>
          <Menu
            anchorEl={csvMenuAnchor}
            open={Boolean(csvMenuAnchor)}
            onClose={() => setCsvMenuAnchor(null)}
          >
            <MenuItem onClick={() => handleDownloadCsv('visible')}>
              表示中の車両（{sortedVehicles.length}台）
            </MenuItem>
            <MenuItem onClick={() => handleDownloadCsv('all')}>
              全車両（{vehicles.length}台・利用外を含む）
            </MenuItem>
          </Menu>
          <Button
            variant="outlined"
            startIcon={<SortIcon />}
            onClick={() => setOrderDialogOpen(true)}
            size="medium"
          >
            車両順番
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box sx={{ width: { xs: '100%', md: 250 } }}>
            <Autocomplete
              multiple
              options={vehicleStatusList.filter(s => s.id !== 3)}
              getOptionLabel={(option) => option.label}
              value={statusFilter}
              onChange={(event, newValue) => {
                setStatusFilter(newValue);
              }}
              renderInput={(params) => (
                <TextField {...params} label="ステータス" placeholder="選択" />
              )}
              size="small"
            />
          </Box>
          <Box sx={{ width: { xs: '100%', md: 250 } }}>
            <Autocomplete
              multiple
              options={vehicleTypeOptions}
              getOptionLabel={(option) => option.name}
              value={vehicleTypeFilter}
              onChange={(event, newValue) => {
                setVehicleTypeFilter(newValue);
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    {option.code && (
                      <Typography variant="caption" color="text.secondary">
                        {option.code}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="車両タイプ" placeholder="選択" />
              )}
              size="small"
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <ToggleButtonGroup
              value={completionFilter}
              exclusive
              onChange={(e, newVal) => {
                if (newVal !== null) setCompletionFilter(newVal);
              }}
              size="small"
              color="primary"
              sx={{ flexWrap: 'wrap' }}
            >
              <ToggleButton value="all">全て</ToggleButton>
              <ToggleButton value="ok">All OKのみ</ToggleButton>
              <ToggleButton value="incomplete">未完了のみ</ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
              <Typography variant="body2" color="text.secondary" component="span">
                {sortedVehicles.length} 台表示中
              </Typography>
              {reservationVehicleIds !== null && (
                <Chip
                  label="予約絞込み中"
                  size="small"
                  color="primary"
                  onDelete={clearReservationFilter}
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          </Box>
        </Stack>

        {/* 予約ベースのフィルター */}
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          予約で絞り込み（期間内に予約がある車両のみ表示）
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          {/* 日付範囲選択 */}
          <Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CalendarMonthIcon />}
              onClick={(e) => setDateRangeAnchor(e.currentTarget)}
              sx={{ minWidth: 200, justifyContent: 'flex-start' }}
            >
              {rentalDateFrom && rentalDateTo
                ? `${rentalDateFrom} 〜 ${rentalDateTo}`
                : '期間を選択'
              }
            </Button>
            <Popover
              open={Boolean(dateRangeAnchor)}
              anchorEl={dateRangeAnchor}
              onClose={() => setDateRangeAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle2">期間を選択</Typography>
                <TextField
                  label="開始日"
                  type="date"
                  value={rentalDateFrom}
                  onChange={(e) => setRentalDateFrom(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="終了日"
                  type="date"
                  value={rentalDateTo}
                  onChange={(e) => setRentalDateTo(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setDateRangeAnchor(null)}
                >
                  適用
                </Button>
              </Box>
            </Popover>
          </Box>
          <Button
            variant={reservationVehicleIds !== null ? 'contained' : 'outlined'}
            color={reservationVehicleIds !== null ? 'primary' : 'inherit'}
            onClick={applyReservationFilter}
            disabled={reservationFilterLoading}
            size="small"
          >
            {reservationFilterLoading
              ? '読込中...'
              : reservationVehicleIds !== null
                ? `${reservationVehicleIds.size}件`
                : '絞り込み'
            }
          </Button>
          {(reservationVehicleIds !== null || departureSortMode !== 'off' || checkSortOn || statusFilter.length > 0 || vehicleTypeFilter.length > 0 || completionFilter !== 'all') && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                clearReservationFilter();
                setDepartureSortMode('off');
                setCheckSortOn(false);
                setStatusFilter([]);
                setVehicleTypeFilter([]);
                setCompletionFilter('all');
              }}
              size="small"
            >
              クリア
            </Button>
          )}
        </Stack>
      </Paper>

      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 250px)' }}>
        <Table stickyHeader sx={{ minWidth: 650 }} size="small" aria-label="vehicle table">
          <TableHead>
            <TableRow>
              {/* 車両タイプ: PC=sticky left:0+top, モバイル=非表示（車両コードに統合） */}
              {!isMobile && (
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', whiteSpace: 'nowrap', position: 'sticky', left: 0, top: 0, zIndex: 4, borderRight: '1px solid #e0e0e0', width: 120, minWidth: 120, py: 0.5, px: 1 }}>車両タイプ</TableCell>
              )}
              {/* 車両コード: sticky left+top */}
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', whiteSpace: 'nowrap', position: 'sticky', left: isMobile ? 0 : 120, top: 0, zIndex: 4, borderRight: '1px solid #e0e0e0', width: isMobile ? 150 : 120, minWidth: isMobile ? 150 : 120, py: 0.5, px: 1 }}>車両コード</TableCell>
              {/* 次回出発: sticky top のみ（左固定しない） */}
              <TableCell
                onClick={() => setDepartureSortMode(prev => prev === 'off' ? 'asc' : prev === 'asc' ? 'desc' : 'off')}
                sx={{ fontWeight: 'bold', backgroundColor: departureSortMode !== 'off' ? '#e3f2fd' : '#f5f5f5', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2, borderRight: '1px solid #e0e0e0', width: 100, minWidth: 100, py: 0.5, px: 1, fontSize: '0.75rem', cursor: 'pointer', userSelect: 'none' }}
              >
                次回出発 {departureSortMode === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> : departureSortMode === 'desc' ? <ArrowDownwardIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> : <SwapVertIcon sx={{ fontSize: 14, verticalAlign: 'middle', opacity: 0.4 }} />}
              </TableCell>
              {/* 一括: sticky top のみ */}
              <TableCell
                align="center"
                onClick={() => setCheckSortOn(prev => !prev)}
                sx={{ fontWeight: 'bold', backgroundColor: checkSortOn ? '#fff3e0' : '#f5f5f5', minWidth: 70, width: 70, position: 'sticky', top: 0, zIndex: 2, borderRight: '1px solid #e0e0e0', py: 0.5, px: 0.5, cursor: 'pointer', userSelect: 'none' }}
              >
                一括 {checkSortOn ? <ArrowUpwardIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> : <SwapVertIcon sx={{ fontSize: 14, verticalAlign: 'middle', opacity: 0.4 }} />}
              </TableCell>
              {/* チェック列: sticky top のみ */}
              {requiredChecks.map((check) => (
                <TableCell
                  key={check.id}
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: check.bg_color || '#f5f5f5',
                    color: check.text_color || 'inherit',
                    minWidth: 52,
                    py: 0.5,
                    px: 0.5,
                    fontSize: '0.7rem',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                  }}
                >
                  {check.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedVehicles.map((vehicle) => (
              <TableRow
                key={vehicle.id}
                hover
                onClick={() => handleRowClick(vehicle)}
                sx={{ cursor: 'pointer' }}
              >
                {/* 車両タイプ: PC のみ表示 */}
                {!isMobile && (
                  <TableCell sx={{
                    whiteSpace: 'nowrap', position: 'sticky', left: 0, zIndex: 2,
                    backgroundColor: vehicle.vehicle_type?.bg_color || '#fff',
                    color: vehicle.vehicle_type?.text_color || 'inherit',
                    borderRight: '1px solid #e0e0e0', width: 120, minWidth: 120, py: 0.3, px: 1,
                  }}>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.2, fontWeight: 600 }}>
                      {vehicle.vehicle_type?.vehicle_type_name || '-'}
                    </Typography>
                    {vehicle.vehicle_type?.vehicle_type_code && (
                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, fontSize: '0.65rem', lineHeight: 1 }}>
                        {vehicle.vehicle_type.vehicle_type_code}
                      </Typography>
                    )}
                  </TableCell>
                )}
                {/* 車両コード: 常にsticky left */}
                <TableCell sx={{ fontWeight: 'medium', color: 'primary.main', whiteSpace: 'nowrap', position: 'sticky', left: isMobile ? 0 : 120, zIndex: 2, backgroundColor: '#fff', borderRight: '1px solid #e0e0e0', width: isMobile ? 150 : 120, minWidth: isMobile ? 150 : 120, py: 0.3, px: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {isMobile && (
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', lineHeight: 1 }}>
                        {vehicle.vehicle_type?.vehicle_type_name || ''}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {vehicle.vehicle_code}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={(e) => handleStatusClick(e, vehicle)}
                        sx={{
                          fontSize: '0.6rem',
                          padding: '2px 4px',
                          minWidth: '60px',
                          height: '22px',
                          boxShadow: 'none',
                          backgroundColor: vehicleStatusList.find(s => s.id === vehicle.status)?.bg_color || undefined,
                          color: vehicleStatusList.find(s => s.id === vehicle.status)?.text_color || undefined,
                          '&:hover': {
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            backgroundColor: vehicleStatusList.find(s => s.id === vehicle.status)?.bg_color || undefined,
                            filter: 'brightness(0.9)'
                          }
                        }}
                      >
                        {vehicle.status_name || '未設定'}
                      </Button>
                      <Button
                        variant={(() => {
                          const status = locationStatusList?.find(s => s.id === Number(vehicle.location_status));
                          return status?.bg_color ? "contained" : "outlined";
                        })()}
                        size="small"
                        onClick={(e) => handleLocationStatusClick(e, vehicle)}
                        sx={{
                          fontSize: '0.6rem',
                          padding: '2px 4px',
                          minWidth: '40px',
                          height: '22px',
                          boxShadow: 'none',
                          ...(() => {
                            const status = locationStatusList?.find(s => s.id === Number(vehicle.location_status));
                            if (status?.bg_color) {
                              return {
                                backgroundColor: status.bg_color,
                                borderColor: status.bg_color,
                                color: status.text_color || '#fff',
                                '&:hover': {
                                  backgroundColor: status.bg_color,
                                  filter: 'brightness(0.9)',
                                }
                              };
                            }
                            return {
                              backgroundColor: vehicle.location_status ? '#e3f2fd' : '#f5f5f5',
                              borderColor: vehicle.location_status ? '#1565c0' : '#bdbdbd',
                              color: vehicle.location_status ? '#1565c0' : '#757575',
                              '&:hover': {
                                backgroundColor: vehicle.location_status ? '#bbdefb' : '#eeeeee',
                              }
                            };
                          })()
                        }}
                      >
                        {locationStatusList.find(s => s.id === Number(vehicle.location_status))?.label || '-'}
                      </Button>
                    </Stack>
                  </Box>
                </TableCell>
                {/* 次回出発 */}
                <TableCell sx={{ whiteSpace: 'nowrap', borderRight: '1px solid #e0e0e0', width: 100, minWidth: 100, py: 0.3, px: 1 }}>
                  {(() => {
                    const next = nextDepartureMap[vehicle.id];
                    if (!next) return <Typography variant="caption" sx={{ color: '#bbb', fontSize: '0.7rem' }}>-</Typography>;
                    const d = dayjs(next.start_date);
                    const today = dayjs();
                    const isToday = d.isSame(today, 'day');
                    const isTomorrow = d.isSame(today.add(1, 'day'), 'day');
                    // 返却日時のフォーマット
                    const retEl = (() => {
                      if (!next.return_date) return null;
                      const r = dayjs(next.return_date);
                      if (!r.isValid()) return null;
                      const rIsToday = r.isSame(today, 'day');
                      const rIsTomorrow = r.isSame(today.add(1, 'day'), 'day');
                      const rLabel = rIsToday ? `今日${r.format('HH:mm')}` : rIsTomorrow ? `明日${r.format('HH:mm')}` : `${r.format('M/D')} ${r.format('HH:mm')}`;
                      return `(返却 ${rLabel})`;
                    })();
                    return (
                      <>
                        <Typography variant="caption" sx={{
                          fontWeight: 600, fontSize: '0.7rem',
                          color: isToday ? '#d32f2f' : isTomorrow ? '#1976d2' : '#666',
                          display: 'block', lineHeight: 1.2,
                        }}>
                          {isToday ? '今日' : isTomorrow ? '明日' : d.format('M/D')} {d.format('HH:mm')}
                        </Typography>
                        {retEl && (
                          <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#333', display: 'block', lineHeight: 1.1 }}>
                            {retEl}
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#999', lineHeight: 1 }}>
                          {next.customer_name}
                        </Typography>
                      </>
                    );
                  })()}
                </TableCell>
                {/* 一括ボタン */}
                {(() => {
                  const inUse = isVehicleInUse(vehicle);
                  const allOk = isAllChecked(vehicle);
                  const hasAnyCheck = requiredChecks.some(c => isCheckDone(vehicle, c.id));
                  const showReset = inUse && hasAnyCheck;
                  return (
                    <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', width: 70, minWidth: 70, py: 0.3, px: 0.5 }}>
                      {showReset ? (
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          onClick={(e) => handleResetAll(e, vehicle)}
                          sx={{ fontSize: '0.6rem', padding: '1px 6px', minWidth: 0, height: 22, whiteSpace: 'nowrap', boxShadow: 'none' }}
                        >
                          RESET
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          disabled={allOk || inUse}
                          onClick={(e) => {
                            if (inUse) { e.stopPropagation(); window.alert('車両が出発中です'); return; }
                            handleAllOk(e, vehicle);
                          }}
                          sx={{
                            fontSize: '0.6rem', padding: '1px 6px', minWidth: 0, height: 22, whiteSpace: 'nowrap', boxShadow: 'none',
                            backgroundColor: allOk ? '#2e7d32' : inUse ? '#9e9e9e' : '#1976d2',
                            color: '#fff',
                            '&.Mui-disabled': { backgroundColor: allOk ? '#2e7d32' : '#9e9e9e', color: '#fff', opacity: 0.8 },
                          }}
                        >
                          {allOk ? 'ALL OK' : 'ALL OK?'}
                        </Button>
                      )}
                    </TableCell>
                  );
                })()}
                {/* チェックボタン */}
                {requiredChecks.map((check) => {
                  const done = isCheckDone(vehicle, check.id);
                  const inUse = isVehicleInUse(vehicle);
                  return (
                    <TableCell key={check.id} align="center" sx={{ backgroundColor: check.bg_color ? `${check.bg_color}${done ? '30' : '15'}` : 'inherit', py: 0.3, px: 0.5 }}>
                      <Button
                        variant={done ? 'contained' : 'outlined'}
                        size="small"
                        disabled={inUse}
                        onClick={(e) => handleDynamicCheckChange(e, vehicle, check.id)}
                        sx={{
                          fontSize: '0.6rem',
                          padding: '1px 0',
                          minWidth: 36,
                          height: 22,
                          boxShadow: 'none',
                          fontWeight: 600,
                          ...(done ? {
                            backgroundColor: '#2e7d32',
                            color: '#fff',
                            '&:hover': { backgroundColor: '#1b5e20', boxShadow: 'none' },
                            '&.Mui-disabled': { backgroundColor: '#2e7d32', color: '#fff', opacity: 0.6 },
                          } : {
                            borderColor: inUse ? '#ddd' : '#ccc',
                            color: inUse ? '#ddd' : '#bbb',
                            backgroundColor: 'transparent',
                            '&:hover': { borderColor: '#999', color: '#666', backgroundColor: '#f5f5f5' },
                          }),
                        }}
                      >
                        OK
                      </Button>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            {sortedVehicles.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  条件に一致する車両が見つかりませんでした。
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {
        retiredVehicles.length > 0 && (
          <>
            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold', color: 'text.secondary', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
              アーカイブ（利用外）
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: '400px', opacity: 0.7, mb: 4, overflowX: 'auto' }}>
              <Table stickyHeader size="small" aria-label="retired vehicle table" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#eeeeee', whiteSpace: 'nowrap', position: isMobile ? 'static' : 'sticky', left: 0, zIndex: 3, borderRight: '1px solid #d0d0d0', width: 120, minWidth: 120, py: 0.5, px: 1 }}>車両タイプ</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#eeeeee', whiteSpace: 'nowrap', position: isMobile ? 'static' : 'sticky', left: 120, zIndex: 3, borderRight: '1px solid #d0d0d0', width: 120, minWidth: 120, py: 0.5, px: 1 }}>車両コード</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#eeeeee', whiteSpace: 'nowrap', position: isMobile ? 'static' : 'sticky', left: 240, zIndex: 3, borderRight: '1px solid #d0d0d0', width: 100, minWidth: 100, py: 0.5, px: 1, fontSize: '0.75rem' }}>次回出発</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#eeeeee', minWidth: 70, width: 70, position: isMobile ? 'static' : 'sticky', left: 340, zIndex: 3, borderRight: '1px solid #d0d0d0', py: 0.5, px: 0.5 }}>一括</TableCell>
                    {requiredChecks.map((check) => (
                      <TableCell
                        key={check.id}
                        align="center"
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: check.bg_color || '#eeeeee',
                          color: check.text_color || 'inherit',
                          minWidth: 52,
                          py: 0.5,
                          px: 0.5,
                          fontSize: '0.7rem',
                        }}
                      >
                        {check.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {retiredVehicles.map((vehicle) => (
                    <TableRow
                      key={vehicle.id}
                      hover
                      onClick={() => handleRowClick(vehicle)}
                      sx={{ cursor: 'pointer', backgroundColor: '#f9f9f9' }}
                    >
                      <TableCell sx={{
                        whiteSpace: 'nowrap', position: isMobile ? 'static' : 'sticky', left: 0, zIndex: 2,
                        backgroundColor: vehicle.vehicle_type?.bg_color || '#f9f9f9',
                        color: vehicle.vehicle_type?.text_color || 'inherit',
                        borderRight: '1px solid #d0d0d0', width: 120, minWidth: 120, py: 0.3, px: 1, opacity: 0.6,
                      }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.2, fontWeight: 600 }}>
                          {vehicle.vehicle_type?.vehicle_type_name || '-'}
                        </Typography>
                        {vehicle.vehicle_type?.vehicle_type_code && (
                          <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, fontSize: '0.65rem', lineHeight: 1 }}>
                            {vehicle.vehicle_type.vehicle_type_code}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'medium', color: 'text.secondary', whiteSpace: 'nowrap', position: isMobile ? 'static' : 'sticky', left: 120, zIndex: 2, backgroundColor: '#f9f9f9', borderRight: '1px solid #d0d0d0', width: 120, minWidth: 120, py: 0.3, px: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {vehicle.vehicle_code}
                          </Typography>
                          <Stack direction="row" spacing={0.5}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={(e) => handleStatusClick(e, vehicle)}
                              sx={{
                                fontSize: '0.6rem',
                                padding: '2px 4px',
                                minWidth: '60px',
                                height: '22px',
                                boxShadow: 'none',
                                backgroundColor: vehicleStatusList.find(s => s.id === vehicle.status)?.bg_color || '#9e9e9e',
                                color: vehicleStatusList.find(s => s.id === vehicle.status)?.text_color || 'white',
                                '&:hover': {
                                  backgroundColor: vehicleStatusList.find(s => s.id === vehicle.status)?.bg_color || '#757575',
                                  filter: 'brightness(0.9)'
                                }
                              }}
                            >
                              {vehicle.status_name || '利用外'}
                            </Button>
                            <Button
                              variant={(() => {
                                const status = locationStatusList?.find(s => s.id === Number(vehicle.location_status));
                                return status?.bg_color ? "contained" : "outlined";
                              })()}
                              size="small"
                              onClick={(e) => handleLocationStatusClick(e, vehicle)}
                              sx={{
                                fontSize: '0.6rem',
                                padding: '2px 4px',
                                minWidth: '40px',
                                height: '22px',
                                boxShadow: 'none',
                                ...(() => {
                                  const status = locationStatusList?.find(s => s.id === Number(vehicle.location_status));
                                  if (status?.bg_color) {
                                    return {
                                      backgroundColor: status.bg_color,
                                      borderColor: status.bg_color,
                                      color: status.text_color || '#fff',
                                      '&:hover': {
                                        backgroundColor: status.bg_color,
                                        filter: 'brightness(0.9)',
                                      }
                                    };
                                  }
                                  return {
                                    backgroundColor: vehicle.location_status ? '#e3f2fd' : '#f5f5f5',
                                    borderColor: vehicle.location_status ? '#1565c0' : '#bdbdbd',
                                    color: vehicle.location_status ? '#1565c0' : '#757575',
                                    '&:hover': {
                                      backgroundColor: vehicle.location_status ? '#bbdefb' : '#eeeeee',
                                    }
                                  };
                                })()
                              }}
                            >
                              {locationStatusList.find(s => s.id === Number(vehicle.location_status))?.label || '-'}
                            </Button>
                          </Stack>
                        </Box>
                      </TableCell>
                      {/* 次回出発（アーカイブ） */}
                      <TableCell sx={{ whiteSpace: 'nowrap', position: isMobile ? 'static' : 'sticky', left: 240, zIndex: 2, backgroundColor: '#f9f9f9', borderRight: '1px solid #d0d0d0', width: 100, minWidth: 100, py: 0.3, px: 1 }}>
                        <Typography variant="caption" sx={{ color: '#bbb', fontSize: '0.7rem' }}>-</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ position: isMobile ? 'static' : 'sticky', left: 340, zIndex: 2, backgroundColor: '#f9f9f9', borderRight: '1px solid #d0d0d0', width: 70, minWidth: 70, py: 0.3, px: 0.5 }}>
                        <Button
                          variant="contained"
                          size="small"
                          color="inherit"
                          disabled
                          sx={{ fontSize: '0.6rem', padding: '1px 6px', minWidth: 0, height: 22, whiteSpace: 'nowrap', boxShadow: 'none' }}
                        >
                          ALL OK
                        </Button>
                      </TableCell>
                      {requiredChecks.map((check) => {
                        const done = isCheckDone(vehicle, check.id);
                        return (
                          <TableCell key={check.id} align="center" sx={{ backgroundColor: check.bg_color ? `${check.bg_color}${done ? '30' : '15'}` : 'inherit', py: 0.3, px: 0.5 }}>
                            <Button
                              variant={done ? 'contained' : 'outlined'}
                              size="small"
                              disabled
                              sx={{
                                fontSize: '0.6rem',
                                padding: '1px 0',
                                minWidth: 36,
                                height: 22,
                                boxShadow: 'none',
                                fontWeight: 600,
                                ...(done ? {
                                  backgroundColor: '#2e7d32',
                                  color: '#fff',
                                  '&.Mui-disabled': { backgroundColor: '#2e7d32', color: '#fff', opacity: 0.7 },
                                } : {
                                  borderColor: '#ccc',
                                  color: '#bbb',
                                }),
                              }}
                            >
                              OK
                            </Button>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )
      }

      <VehicleDetailPanel
        open={Boolean(selectedVehicle)}
        vehicle={selectedVehicle}
        onClose={handleClosePanel}
        onSave={handleSaveVehicle}
        onDirtyChange={setVehiclePanelDirty}
        vehicleStatusList={vehicleStatusList}
        locationStatusList={locationStatusList}
      />

      {/* 未保存確認ダイアログ */}
      {
        unsavedConfirm && (
          <Dialog open={true} onClose={() => handleUnsavedConfirmAction('cancel')}>
            <DialogTitle>
              未保存の情報がありますが{unsavedConfirm.targetVehicle ? '新しいパネルを開きますか？' : '閉じますか？'}
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                未保存の変更があります。
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleUnsavedConfirmAction('cancel')}>いいえ</Button>
              <Button onClick={() => handleUnsavedConfirmAction('discard')} variant="contained">
                破棄して{unsavedConfirm.targetVehicle ? '開く' : '閉じる'}
              </Button>
            </DialogActions>
          </Dialog>
        )
      }

      <Dialog open={statusModal.open} onClose={() => setStatusModal({ ...statusModal, open: false })}>
        <DialogTitle>ステータス変更</DialogTitle>
        <DialogContent>
          <Grid container spacing={1} sx={{ mt: 1 }}>
            {vehicleStatusList.map((status) => (
              <Grid {...{item: true, xs: 4} as any} key={status.id}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleStatusUpdate(status.id)}
                  sx={{
                    height: '100%',
                    backgroundColor: status.bg_color || undefined,
                    color: status.text_color || undefined,
                    '&:hover': {
                      backgroundColor: status.bg_color || undefined,
                      filter: 'brightness(0.9)',
                    }
                  }}
                >
                  {status.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      <Dialog open={locationStatusModal.open} onClose={() => setLocationStatusModal({ ...locationStatusModal, open: false })}>
        <DialogTitle>位置ステータス変更</DialogTitle>
        <DialogContent>
          <Grid container spacing={1} sx={{ mt: 1 }}>
            {locationStatusList.map((status) => (
              <Grid {...{item: true, xs: 4} as any} key={status.id ?? 'null'}>
                <Button
                  variant={status.id === null ? "outlined" : "contained"}
                  fullWidth
                  onClick={() => handleLocationStatusUpdate(status.id)}
                  sx={{
                    height: '100%',
                    backgroundColor: status.bg_color || undefined,
                    color: status.text_color || undefined,
                    '&:hover': {
                      backgroundColor: status.bg_color || undefined,
                      filter: 'brightness(0.9)',
                    }
                  }}
                >
                  {status.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            チェックを初期化しますか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleConfirmClose(false)} color="primary">
            いいえ (そのまま)
          </Button>
          <Button onClick={() => handleConfirmClose(true)} color="primary" autoFocus>
            はい (初期化)
          </Button>
        </DialogActions>
      </Dialog>

      {/* 車両順番編集ダイアログ */}
      <VehicleOrderDialog
        open={orderDialogOpen}
        onClose={() => setOrderDialogOpen(false)}
        vehicleTypes={vehicleTypeOptions}
        vehicles={activeVehicles}
        onSave={handleSaveVehicleOrder}
        onSaveVehicleTypes={handleSaveVehicleTypeOrder}
      />

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity as any} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box >
  );
}
